
import React, { useState } from 'react';
import { ArrowLeftRight, Search, Plus, ShieldAlert, CheckCircle2, XCircle, Clock, Eye, Ban, Trash2, Database, RefreshCw, Layers } from 'lucide-react';
import { useP2P, P2PListing } from '@/shared/hooks/useP2P';
import { seedP2PAds, clearP2PAds } from '@/shared/utils/seedP2P';
import { cn } from '@/shared/lib/utils';

const AdminP2PPage: React.FC = () => {
    const { allListings, allTrades, loading, addAd, updateAd, deleteAd, updateTradeStatus } = useP2P();
    const [activeTab, setActiveTab] = useState('listings');
    const [isSeeding, setIsSeeding] = useState(false);

    const handleSeed = async (count: number) => {
        if (!window.confirm(`${count}개의 무작위 P2P 광고를 생성하시겠습니까?`)) return;
        setIsSeeding(true);
        try {
            await seedP2PAds(count);
            alert(`${count}개의 샘플 데이터가 성공적으로 생성되었습니다.`);
        } catch (e: any) {
            alert('생성 중 오류 발생: ' + e.message);
        }
        setIsSeeding(false);
    };

    const handleClear = async () => {
        if (!window.confirm('모든 P2P 광고 데이터를 삭제하시겠습니까?')) return;
        setIsSeeding(true);
        try {
            await clearP2PAds();
            alert('모든 광고 데이터가 삭제되었습니다.');
        } catch (e: any) {
            alert('삭제 중 오류 발생: ' + e.message);
        }
        setIsSeeding(false);
    };

    if (loading) return <div className="p-8 text-white uppercase font-black animate-pulse">P2P 시스템 로딩중...</div>;

    return (
        <div className="space-y-8 animate-in fade-in duration-700">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div className="space-y-1">
                    <h1 className="text-2xl font-black flex items-center gap-3">
                        <ArrowLeftRight className="h-7 w-7 text-emerald-500" /> P2P 거래 및 마켓 관리
                    </h1>
                    <p className="text-slate-500 font-bold text-sm uppercase tracking-widest">P2P 마켓 관리 센터</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {/* 샘플 데이터 대량 생성 도구 */}
                    <div className="flex items-center gap-2 bg-slate-900 p-1.5 rounded-xl border border-slate-800">
                        <span className="text-[9px] font-black text-slate-500 uppercase px-2">무작위 생성:</span>
                        <button
                            onClick={() => handleSeed(10)}
                            disabled={isSeeding}
                            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-[10px] font-black uppercase transition-all flex items-center gap-2"
                        >
                            <Database className="h-3 w-3" /> 10개
                        </button>
                        <button
                            onClick={() => handleSeed(20)}
                            disabled={isSeeding}
                            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-[10px] font-black uppercase transition-all flex items-center gap-2"
                        >
                            <Layers className="h-3 w-3" /> 20개
                        </button>
                        <button
                            onClick={handleClear}
                            disabled={isSeeding}
                            className="p-2 bg-rose-500/10 hover:bg-rose-500 hover:text-white text-rose-500 rounded-lg transition-all"
                            title="전체 삭제"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>

                    <button className="h-11 px-6 rounded-xl bg-emerald-600 text-white font-black text-xs uppercase tracking-widest flex items-center gap-2 hover:bg-emerald-500 transition-all shadow-xl shadow-emerald-600/20">
                        <Plus className="h-4 w-4" /> 광고 직접 등록
                    </button>
                </div>
            </div>

            <div className="flex gap-1 p-1 bg-slate-950 border border-slate-800 rounded-2xl w-fit">
                {[
                    { id: 'listings', label: '실시간 광고 목록' },
                    { id: 'active_trades', label: '전체 거래 내역' },
                    { id: 'disputes', label: '분쟁 건 관리' }
                ].map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={cn(
                            "px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all",
                            activeTab === tab.id ? "bg-emerald-600 text-white shadow-lg shadow-emerald-600/20" : "text-slate-500 hover:text-white"
                        )}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="rounded-[2rem] bg-slate-950 border border-slate-800 overflow-hidden shadow-xl">
                {activeTab === 'listings' ? (
                    <table className="w-full text-xs">
                        <thead className="bg-[#0f0f12] text-slate-500 font-black uppercase tracking-widest border-b border-slate-800">
                            <tr>
                                <th className="px-8 py-4 text-left">광고주 / 코인</th>
                                <th className="px-8 py-4 text-left">유형</th>
                                <th className="px-8 py-4 text-right">잔여 수량</th>
                                <th className="px-8 py-4 text-right">거래 단가</th>
                                <th className="px-8 py-4 text-right">한도액 (Min~Max)</th>
                                <th className="px-8 py-4 text-center">상태</th>
                                <th className="px-8 py-4 text-right">관리</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800 font-medium">
                            {allListings.map((list) => (
                                <tr key={list.id} className="hover:bg-white/[0.01] transition-colors group">
                                    <td className="px-8 py-5">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-blue-500 font-black uppercase">
                                                {list.asset.slice(0, 2)}
                                            </div>
                                            <div>
                                                <p className="font-bold text-white uppercase leading-none">{list.advertiserName}</p>
                                                <p className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest">{list.asset}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5">
                                        <span className={cn("font-black uppercase text-[10px]", list.type === 'SELL' ? 'text-rose-500' : 'text-emerald-500')}>
                                            {list.type === 'SELL' ? '판매중' : '구매중'}
                                        </span>
                                    </td>
                                    <td className="px-8 py-5 text-right font-black text-white tabular-nums">{list.available.toLocaleString()}</td>
                                    <td className="px-8 py-5 text-right font-black text-emerald-500 tabular-nums">₩{list.price.toLocaleString()}</td>
                                    <td className="px-8 py-5 text-right text-slate-400 tabular-nums font-bold">
                                        ₩{list.limitMin.toLocaleString()} ~ ₩{list.limitMax.toLocaleString()}
                                    </td>
                                    <td className="px-8 py-5 text-center">
                                        <button
                                            onClick={() => updateAd(list.id, { status: list.status === 'Active' ? 'Inactive' : 'Active' })}
                                            className={cn(
                                                "px-3 py-1 rounded-full text-[9px] font-black uppercase transition-all",
                                                list.status === 'Active' ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20' : 'bg-slate-800 text-slate-500 border border-slate-700'
                                            )}>
                                            {list.status === 'Active' ? '활성' : '비활성'}
                                        </button>
                                    </td>
                                    <td className="px-8 py-5 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => deleteAd(list.id)}
                                                className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-slate-500 hover:text-rose-500 transition-all"
                                                title="광고 삭제"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {allListings.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="px-8 py-20 text-center text-slate-500 font-bold uppercase tracking-widest italic">
                                        등록된 P2P 광고가 없습니다. 상단의 샘플 생성 기능을 이용해 보세요.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                ) : activeTab === 'active_trades' ? (
                    <table className="w-full text-xs">
                        <thead className="bg-[#0f0f12] text-slate-500 font-black uppercase tracking-widest border-b border-slate-800">
                            <tr>
                                <th className="px-8 py-4 text-left">거래 ID / 유저</th>
                                <th className="px-8 py-4 text-left">코인 / 유형</th>
                                <th className="px-8 py-4 text-right">거래 금액</th>
                                <th className="px-8 py-4 text-center">신청 일시</th>
                                <th className="px-8 py-4 text-center">상태</th>
                                <th className="px-8 py-4 text-right">조치</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800 font-medium">
                            {allTrades.map((trade) => (
                                <tr key={trade.id} className="hover:bg-white/[0.01] transition-colors group">
                                    <td className="px-8 py-5">
                                        <p className="font-mono text-slate-500 text-[10px] tracking-tighter mb-1">{trade.id}</p>
                                        <p className="font-bold text-white uppercase">{trade.userId}</p>
                                    </td>
                                    <td className="px-8 py-5">
                                        <div className="flex items-center gap-2">
                                            <span className="px-2 py-0.5 rounded bg-white/5 text-slate-300 font-black text-[9px] uppercase">{trade.asset}</span>
                                            <span className={cn("font-black text-[9px] uppercase", trade.type === 'SELL' ? 'text-rose-500' : 'text-emerald-500')}>
                                                {trade.type}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5 text-right font-black">
                                        <p className="text-white tabular-nums italic">₩{trade.totalKrw.toLocaleString()}</p>
                                        <p className="text-slate-500 text-[10px] tabular-nums mt-0.5">{trade.totalCoin.toFixed(4)} {trade.asset}</p>
                                    </td>
                                    <td className="px-8 py-5 text-center text-slate-500 tabular-nums font-bold">
                                        {trade.createdAt?.toDate?.()?.toLocaleString() || '처리중...'}
                                    </td>
                                    <td className="px-8 py-5 text-center">
                                        <div className={cn(
                                            "inline-flex px-3 py-1 rounded-full text-[9px] font-black uppercase",
                                            trade.status === 'COMPLETED' ? 'bg-accent text-slate-400' : 'bg-blue-500/10 text-blue-500'
                                        )}>
                                            {trade.status}
                                        </div>
                                    </td>
                                    <td className="px-8 py-5 text-right">
                                        <div className="flex justify-end gap-2 text-right">
                                            {trade.status === 'COMPLETED' ? (
                                                <button
                                                    onClick={() => {
                                                        if (window.confirm('자산 및 트랜잭션 상태를 다시 동기화하시겠습니까?')) {
                                                            updateTradeStatus(trade.id, 'COMPLETED');
                                                        }
                                                    }}
                                                    className="h-8 px-4 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 font-black text-[9px] uppercase transition-all flex items-center gap-2"
                                                >
                                                    <RefreshCw className="h-3 w-3" /> 데이터 동기화
                                                </button>
                                            ) : (
                                                <>
                                                    <button
                                                        onClick={() => updateTradeStatus(trade.id, 'COMPLETED')}
                                                        className="h-8 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-black text-[9px] uppercase transition-all"
                                                    >
                                                        거래 승인
                                                    </button>
                                                    <button
                                                        onClick={() => updateTradeStatus(trade.id, 'CANCELLED')}
                                                        className="h-8 px-4 rounded-lg bg-slate-800 hover:bg-rose-600 text-slate-300 hover:text-white font-black text-[9px] uppercase transition-all"
                                                    >
                                                        거절
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {allTrades.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-8 py-20 text-center text-slate-500 font-bold uppercase tracking-widest italic leading-relaxed">
                                        아직 발생한 거래 내역이 없습니다.<br />유저가 P2P 마켓에서 거래를 신청하면 이곳에 노출됩니다.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                ) : (
                    <div className="p-20 text-center space-y-6">
                        <div className="w-20 h-20 rounded-full bg-slate-950 border border-slate-800 flex items-center justify-center mx-auto shadow-2xl">
                            <ShieldAlert className="h-10 w-10 text-slate-700" />
                        </div>
                        <div className="max-w-md mx-auto">
                            <h3 className="text-base font-black text-white uppercase tracking-widest italic">P2P 분쟁 해결 센터</h3>
                            <p className="text-[11px] text-slate-500 mt-2 font-medium leading-relaxed uppercase">사용자 간 입금 미인증 또는 자산 전송 지연 등의 이슈가 발생할 경우 관리자가 직접 개입하여 강제로 거래를 종료하거나 자산을 반환할 수 있는 기능입니다.</p>
                        </div>
                        <button className="h-12 px-8 rounded-2xl bg-slate-900 border border-slate-800 text-slate-300 font-black text-[10px] uppercase tracking-widest hover:bg-slate-800 transition-all flex items-center gap-3 mx-auto shadow-xl">
                            <Clock className="h-4 w-4" /> 가동 대기 중 (인증 필요)
                        </button>
                    </div>
                )}
            </div>

            <div className="p-8 rounded-[2.5rem] bg-emerald-500/5 border border-emerald-500/10 flex items-start gap-6">
                <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 flex items-center justify-center shrink-0 border border-emerald-500/20">
                    <ShieldAlert className="h-7 w-7 text-emerald-500" />
                </div>
                <div className="space-y-1.5 pt-1">
                    <h4 className="font-black text-emerald-500 text-sm uppercase tracking-tighter">P2P 운영 방침</h4>
                    <p className="text-[11px] text-emerald-500/70 font-bold leading-relaxed uppercase tracking-tight italic">
                        현재 모든 거래 글의 '상태'가 활성(Active)인 경우에만 유저 대시보드에 노출됩니다. 관리자는 비정상적인 거래글을 즉시 비활성(Inactive)으로 변경하거나 삭제하여 시장의 건전성을 보호해야 합니다. 모든 거래 승인 시 유저의 자산 변동 여부를 반드시 교차 검증(Cross-Check) 하십시오.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AdminP2PPage;
